import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { LOG } from "../utils/logUtil";
import { useAppLoading } from "./useAppLoading";
import axios from "axios";
import { IUser } from "./useUsers";
import { useCurrentLocation } from "./useCurrentLocation";

interface IDashboardStats {
  totalUsers: number;
  donatingAccounts: number;
  totalDrops: number;
  totalTomraDrops: number;
  totalCO2Saved: number;
  totalDropsAmount: {
    total: number;
  }[];
  totalDropsWeight: {
    total: number;
  }[];
  totalBeevisionDropWeight: {
    total: number;
  }[];
  leadingSources: {
    [key: string]: number;
  };
  houseHolds: {
    [key: string]: number;
  };
  usersLeaderboard: IUser[];
  usersLeaderboardByVolume: {
    totalWeight: number;
    userDetails: IUser;
    userId: string;
  }[];
  totalActiveUsers: number;
  totalTomraActiveUsers: number;
  totalBeevisionActiveUsers: number;
  allTotalActiveUsers: number;
  totalPayout: number;
  totalDropsPayout: number;
  totalTomraDropsPayout: number;
  totalAmountDonated: number;
  averageDropsPerDay: number;
  averageStatieDropsPerDay: number;
  statieItemsPerDrop: number;
  averageDropSize: number;
}

interface IDropsLocationStats {
  [key: string]: number;
}

interface IDropsStats {
  dropsByDays: {
    [key: string]: number;
  };
  tomraDropsByDays: {
    [key: string]: number;
  };
  dropsByWeek: {
    [key: string]: number;
  };
  tomraDropsByWeek: {
    [key: string]: number;
  };
  dropsByMonth: {
    [key: string]: number;
  };
  tomraDropsByMonth: {
    [key: string]: number;
  };
  dropsByYear: {
    [key: string]: number;
  };
  tomraDropsByYear: {
    [key: string]: number;
  };
}

interface IStreamCollectedStats {
  dailyStreamCollected: {
    totalWeight: number;
    _id: string;
  }[];
  weeklyStreamCollected: {
    totalWeight: number;
    _id: string;
  }[];
  monthlyStreamCollected: {
    totalWeight: number;
    _id: string;
  }[];
  yearlyStreamCollected: {
    totalWeight: number;
    _id: string;
  }[];
}

interface IActiveUsers {
  weeklyActiveUsers: {
    [key: string]: number;
  };
  monthlyActiveUsers: {
    [key: string]: number;
  };
  yearlyActiveUsers: {
    [key: string]: number;
  };
}

interface ITrendingDrops {
  name: string;
  data: number[];
}

interface IRevenuePerCustomerDrops {
  name: string;
  data: [number, number][]; // [timestamp, revenuePerCustomer]
}

interface IStore {
  activeUsers: IActiveUsers | null;
  trendingDrops: ITrendingDrops[] | null;
  trendingMaterialActiveUsers: ITrendingDrops[] | null;
  activeUsersConversion: ITrendingDrops[] | null;
  regularDroppersMaterialConversion: ITrendingDrops[] | null;
  materialConversion: ITrendingDrops[] | null;
  kgOnlyTrendingDrops: ITrendingDrops[] | null;
  payoutToConsumerTrendline: ITrendingDrops[] | null;
  isLoadingPayoutToConsumer: boolean;
  isPayoutToConsumerError: boolean;
  withdrawsPerLocationTrendline: ITrendingDrops[] | null;
  isLoadingWithdrawsPerLocation: boolean;
  isWithdrawsPerLocationError: boolean;
  withdrawsPerLocationFirstLoadDone: boolean;
  usersWithMoneyPerLocation: ITrendingDrops[] | null;
  isLoadingUsersWithMoneyPerLocation: boolean;
  isUsersWithMoneyPerLocationError: boolean;
  donationsPerCommunityCumulative: ITrendingDrops[] | null;
  isLoadingDonationsPerCommunity: boolean;
  isDonationsPerCommunityError: boolean;
  walletAndDonationsCumulative: ITrendingDrops[] | null;
  isLoadingWalletAndDonations: boolean;
  isWalletAndDonationsError: boolean;
  revenueMarginsTrendline: ITrendingDrops[] | null;
  isLoadingRevenueMargins: boolean;
  isRevenueMarginsError: boolean;
  droppieRevenueTrendline: ITrendingDrops[] | null;
  isLoadingDroppieRevenue: boolean;
  isDroppieRevenueError: boolean;
  droppieRevenueTrendingDrops: ITrendingDrops[] | null;
  totalDroppieRevenueTrendingDrops:
    | {
        name: string;
        value: number;
      }[]
    | null;
  dashboardStats: IDashboardStats | null;
  dropsStats: IDropsStats | null;
  dropsLocationStats: IDropsLocationStats | null;
  streamCollectedStats: IStreamCollectedStats | null;
  revenuePerCustomerStats: IRevenuePerCustomerDrops[] | null;
  getDropsStats: (requestedYear?: number, requestedDay?: Date) => void;
  getDropsStatsByLocation: (
    period?: "yearly" | "monthly" | "weekly",
    date?: Date
  ) => void;
  getStreamCollectedStats: (requestedDay?: Date) => void;
  getRevenuePerCustomerStats: (
    period?: "yearly" | "quarterly" | "monthly" | "weekly",
    year?: number,
    quarter?: "Q1" | "Q2" | "Q3" | "Q4",
    month?: number,
    week?: Date,
    requestedDay?: Date,
    selectedStores?: string[]
  ) => void;
  getStaticStats: (
    period:
      | "AllTime"
      | "Daily"
      | "Weekly"
      | "LastTwelweMonths"
      | "Monthly"
      | "Yearly",
    startDate?: Date,
    endDate?: Date,
    streamFilters?: string[]
  ) => void;
  getTrendingDrops: (
    requestedDay?: Date,
    period?: "daily" | "weekly" | "lastTwelweMonths" | "monthly" | "yearly"
  ) => void;
  getTrendingMaterialActiveUsers: (
    requestedDay?: Date,
    period?: "daily" | "weekly" | "monthly" | "yearly"
  ) => void;
  getActiveUsersConversion: (
    requestedDay?: Date,
    period?: "daily" | "weekly" | "lastTwelweMonths" | "monthly" | "yearly",
    locations?: string[]
  ) => void;
  getRegularDroppersMaterialConversion: (
    requestedDay?: Date,
    period?: "daily" | "weekly" | "lastTwelweMonths" | "monthly" | "yearly",
    locations?: string[]
  ) => void;
  getMaterialConversion: (
    startYear: number,
    startMonth: number,
    endYear: number,
    endMonth: number,
    materialId?: string
  ) => void;
  getActiveUsers: (requestedDay?: Date, dropsCount?: number) => void;
  getKgOnlyTrendingDrops: (
    requestedDay?: Date,
    period?: "daily" | "weekly" | "lastTwelweMonths" | "monthly" | "yearly"
  ) => void;
  getPayoutToConsumerTrendingLine: (
    period: "month" | "week" | "quarter" | "year",
    fromToParams: {
      fromYear: number;
      fromMonth?: number;
      fromWeek?: number;
      fromQuarter?: number;
      toYear: number;
      toMonth?: number;
      toWeek?: number;
      toQuarter?: number;
    },
    type?: "location" | "city",
    places?: string[],
    materialName?: string
  ) => void;
  getRevenueMarginsTrendingLine: (
    period: "month" | "week" | "quarter" | "year",
    fromToParams: {
      fromYear: number;
      fromMonth?: number;
      fromWeek?: number;
      fromQuarter?: number;
      toYear: number;
      toMonth?: number;
      toWeek?: number;
      toQuarter?: number;
    },
    type?: "location" | "city",
    materialName?: string
  ) => void;
  getDroppieRevenueTrendingLine: (
    period: "month" | "week" | "quarter" | "year",
    fromToParams: {
      fromYear: number;
      fromMonth?: number;
      fromWeek?: number;
      fromQuarter?: number;
      toYear: number;
      toMonth?: number;
      toWeek?: number;
      toQuarter?: number;
    },
    type?: "location" | "city",
    materialName?: string
  ) => void;
  getWithdrawsPerLocationTrendingLine: (
    period: "month" | "week" | "quarter" | "year",
    fromToParams: {
      fromYear: number;
      fromMonth?: number;
      fromWeek?: number;
      fromQuarter?: number;
      toYear: number;
      toMonth?: number;
      toWeek?: number;
      toQuarter?: number;
    },
    type?: "location" | "city",
    selectedPlaces?: string[]
  ) => void;
  getDonationsPerCommunityCumulative: (
    period: "month" | "week" | "quarter" | "year",
    fromToParams: {
      fromYear: number;
      fromMonth?: number;
      fromWeek?: number;
      fromQuarter?: number;
      toYear: number;
      toMonth?: number;
      toWeek?: number;
      toQuarter?: number;
    },
    cities?: string[]
  ) => void;
  getWalletAndDonationsCumulative: (
    period: "month" | "week" | "quarter" | "year",
    fromToParams: {
      fromYear: number;
      fromMonth?: number;
      fromWeek?: number;
      fromQuarter?: number;
      toYear: number;
      toMonth?: number;
      toWeek?: number;
      toQuarter?: number;
    }
  ) => void;
  getUsersWithMoneyInWalletPerLocation: (
    period: "month" | "week" | "quarter" | "year",
    fromToParams: {
      fromYear: number;
      fromMonth?: number;
      fromWeek?: number;
      fromQuarter?: number;
      toYear: number;
      toMonth?: number;
      toWeek?: number;
      toQuarter?: number;
    },
    type?: "location" | "city",
    selectedPlaces?: string[]
  ) => void;
  getDroppieRevenueTrendingDrops: (
    requestedDay?: Date,
    period?: "daily" | "weekly" | "lastTwelweMonths" | "monthly" | "yearly"
  ) => void;
  getTotalDroppieRevenueTrendingDrops: (
    requestedDay?: Date,
    period?: "monthly" | "yearly"
  ) => void;
}

export const useDashboardStats = create<IStore>()(
  persist(
    (set) => ({
      /** State */
      activeUsers: null,
      dashboardStats: null,
      trendingDrops: null,
      trendingMaterialActiveUsers: null,
      activeUsersConversion: null,
      regularDroppersMaterialConversion: null,
      materialConversion: null,
      kgOnlyTrendingDrops: null,
      payoutToConsumerTrendline: null,
      isLoadingPayoutToConsumer: false,
      isPayoutToConsumerError: false,
      withdrawsPerLocationTrendline: null,
      isLoadingWithdrawsPerLocation: false,
      isWithdrawsPerLocationError: false,
      withdrawsPerLocationFirstLoadDone: false,
      usersWithMoneyPerLocation: null,
      isLoadingUsersWithMoneyPerLocation: false,
      isUsersWithMoneyPerLocationError: false,
      donationsPerCommunityCumulative: null,
      isLoadingDonationsPerCommunity: false,
      isDonationsPerCommunityError: false,
      walletAndDonationsCumulative: null,
      isLoadingWalletAndDonations: false,
      isWalletAndDonationsError: false,
      revenueMarginsTrendline: null,
      isLoadingRevenueMargins: false,
      isRevenueMarginsError: false,
      droppieRevenueTrendline: null,
      isLoadingDroppieRevenue: false,
      isDroppieRevenueError: false,
      droppieRevenueTrendingDrops: null,
      totalDroppieRevenueTrendingDrops: null,
      dropsStats: null,
      dropsLocationStats: null,
      streamCollectedStats: null,
      revenuePerCustomerStats: null,
      /** Functions */
      getTrendingDrops: async (
        requestedDay = new Date(),
        period = "lastTwelweMonths"
      ) => {
        useAppLoading.getState().setIsLoading(true);
        const currentLocation = useCurrentLocation.getState().currentLocation;
        try {
          const response = await axios.get(
            `/drops/market/stats/trending?requestedDay=${requestedDay}&period=${period}&location=${currentLocation}`
          );
          if (response?.data) {
            set({ trendingDrops: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching drops trending dashboard stats", {
            error,
          });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      getTrendingMaterialActiveUsers: async (
        requestedDay = new Date(),
        period = "monthly"
      ) => {
        useAppLoading.getState().setIsLoading(true);
        const currentLocation = useCurrentLocation.getState().currentLocation;
        try {
          const response = await axios.get(
            `/drops/market/stats/trending/active-users?requestedDay=${requestedDay}&period=${period}&location=${currentLocation}`
          );
          if (response?.data) {
            set({ trendingMaterialActiveUsers: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching drops trending dashboard stats", {
            error,
          });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      getActiveUsersConversion: async (
        requestedDay = new Date(),
        period = "lastTwelweMonths",
        locations = []
      ) => {
        useAppLoading.getState().setIsLoading(true);
        const currentLocation = useCurrentLocation.getState().currentLocation;

        // Build location query parameter
        let locationQuery = currentLocation; // fallback to current location
        if (locations && locations.length > 0) {
          // locations array already contains location IDs
          const filteredLocations = locations.filter((loc) => loc !== "All");
          if (filteredLocations.length > 0) {
            locationQuery = filteredLocations.join(",");
          }
        }

        try {
          const response = await axios.get(
            `/drops/users/stats/percentage-active-users?requestedDay=${requestedDay}&period=${period}&location=${locationQuery}`
          );
          if (response?.data) {
            set({ activeUsersConversion: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching drops trending dashboard stats", {
            error,
          });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      getRegularDroppersMaterialConversion: async (
        requestedDay = new Date(),
        period = "lastTwelweMonths",
        locations = []
      ) => {
        useAppLoading.getState().setIsLoading(true);
        const currentLocation = useCurrentLocation.getState().currentLocation;

        // Build location query parameter
        let locationQuery = currentLocation; // fallback to current location
        if (locations && locations.length > 0) {
          // locations array already contains location IDs
          const filteredLocations = locations.filter((loc) => loc !== "All");
          if (filteredLocations.length > 0) {
            locationQuery = filteredLocations.join(",");
          }
        }

        try {
          const response = await axios.get(
            `/drops/users/stats/percentage-regular-droppers?requestedDay=${requestedDay}&period=${period}&location=${locationQuery}`
          );
          if (response?.data) {
            set({ regularDroppersMaterialConversion: response?.data });
          }
        } catch (error) {
          LOG.error(
            "Error on fetching regular droppers material conversion stats",
            {
              error,
            }
          );
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      getMaterialConversion: async (
        startYear: number,
        startMonth: number,
        endYear: number,
        endMonth: number,
        materialId = ""
      ) => {
        useAppLoading.getState().setIsLoading(true);

        if (!materialId) {
          useAppLoading.getState().setIsLoading(false);
          return;
        }

        try {
          const queryParams = new URLSearchParams({
            startYear: startYear.toString(),
            startMonth: startMonth.toString(),
            endYear: endYear.toString(),
            endMonth: endMonth.toString(),
          });
          const response = await axios.get(
            `/drops/stats/single-material-conversions-per-location/${materialId}?${queryParams.toString()}`
          );
          if (response?.data) {
            set({ materialConversion: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching material conversion stats", {
            error,
          });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      getKgOnlyTrendingDrops: async (
        requestedDay = new Date(),
        period = "lastTwelweMonths"
      ) => {
        useAppLoading.getState().setIsLoading(true);
        const currentLocation = useCurrentLocation.getState().currentLocation;
        try {
          const response = await axios.get(
            `/drops/market/stats/kgtrending?requestedDay=${requestedDay}&period=${period}&location=${currentLocation}`
          );
          if (response?.data) {
            set({ kgOnlyTrendingDrops: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching drops trending dashboard stats", {
            error,
          });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      getPayoutToConsumerTrendingLine: async (
        period: "month" | "week" | "quarter" | "year",
        fromToParams: {
          fromYear: number;
          fromMonth?: number;
          fromWeek?: number;
          fromQuarter?: number;
          toYear: number;
          toMonth?: number;
          toWeek?: number;
          toQuarter?: number;
        },
        type?: "location" | "city",
        places?: string[],
        materialName?: string
      ) => {
        set({ isLoadingPayoutToConsumer: true, isPayoutToConsumerError: false });
        try {
          const params: Record<string, string> = {
            period,
            fromYear: fromToParams.fromYear.toString(),
            toYear: fromToParams.toYear.toString(),
          };
          if (period === "month" || period === "year") {
            params.fromMonth = (fromToParams.fromMonth ?? 1).toString();
            params.toMonth = (fromToParams.toMonth ?? 12).toString();
          } else if (period === "week") {
            params.fromWeek = (fromToParams.fromWeek ?? 1).toString();
            params.toWeek = (fromToParams.toWeek ?? 53).toString();
          } else {
            params.fromQuarter = (fromToParams.fromQuarter ?? 1).toString();
            params.toQuarter = (fromToParams.toQuarter ?? 4).toString();
          }
          if (type) params.type = type;
          if (places && places.length > 0) {
            params.places = places.join(",");
          }
          if (materialName) {
            params.materialName = materialName;
          }
          const queryParams = new URLSearchParams(params);
          const response = await axios.get(
            `/drops/market/stats/revenue-trending-range?${queryParams.toString()}`
          );
          if (response?.data) {
            set({ payoutToConsumerTrendline: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching drops trending dashboard stats", {
            error,
          });
          set({ isPayoutToConsumerError: true });
        } finally {
          set({ isLoadingPayoutToConsumer: false });
        }
      },
      getRevenueMarginsTrendingLine: async (
        period: "month" | "week" | "quarter" | "year",
        fromToParams: {
          fromYear: number;
          fromMonth?: number;
          fromWeek?: number;
          fromQuarter?: number;
          toYear: number;
          toMonth?: number;
          toWeek?: number;
          toQuarter?: number;
        },
        type?: "location" | "city",
        materialName?: string
      ) => {
        set({ isLoadingRevenueMargins: true, isRevenueMarginsError: false });
        try {
          const params: Record<string, string> = {
            period,
            fromYear: fromToParams.fromYear.toString(),
            toYear: fromToParams.toYear.toString(),
          };
          if (period === "month" || period === "year") {
            params.fromMonth = (fromToParams.fromMonth ?? 1).toString();
            params.toMonth = (fromToParams.toMonth ?? 12).toString();
          } else if (period === "week") {
            params.fromWeek = (fromToParams.fromWeek ?? 1).toString();
            params.toWeek = (fromToParams.toWeek ?? 53).toString();
          } else {
            params.fromQuarter = (fromToParams.fromQuarter ?? 1).toString();
            params.toQuarter = (fromToParams.toQuarter ?? 4).toString();
          }
          if (type) params.type = type;
          if (materialName) {
            params.materialName = materialName;
          }
          const queryParams = new URLSearchParams(params);
          const response = await axios.get(
            `/drops/market/stats/revenue-margins-range?${queryParams.toString()}`
          );
          if (response?.data) {
            set({ revenueMarginsTrendline: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching revenue margins", { error });
          set({ isRevenueMarginsError: true });
        } finally {
          set({ isLoadingRevenueMargins: false });
        }
      },
      getDroppieRevenueTrendingLine: async (
        period: "month" | "week" | "quarter" | "year",
        fromToParams: {
          fromYear: number;
          fromMonth?: number;
          fromWeek?: number;
          fromQuarter?: number;
          toYear: number;
          toMonth?: number;
          toWeek?: number;
          toQuarter?: number;
        },
        type?: "location" | "city",
        materialName?: string
      ) => {
        set({ isLoadingDroppieRevenue: true, isDroppieRevenueError: false });
        try {
          const params: Record<string, string> = {
            period,
            fromYear: fromToParams.fromYear.toString(),
            toYear: fromToParams.toYear.toString(),
          };
          if (period === "month" || period === "year") {
            params.fromMonth = (fromToParams.fromMonth ?? 1).toString();
            params.toMonth = (fromToParams.toMonth ?? 12).toString();
          } else if (period === "week") {
            params.fromWeek = (fromToParams.fromWeek ?? 1).toString();
            params.toWeek = (fromToParams.toWeek ?? 53).toString();
          } else {
            params.fromQuarter = (fromToParams.fromQuarter ?? 1).toString();
            params.toQuarter = (fromToParams.toQuarter ?? 4).toString();
          }
          if (type) params.type = type;
          if (materialName) params.materialName = materialName;

          const queryParams = new URLSearchParams(params);
          const response = await axios.get(
            `/drops/market/stats/droppie-revenue-range?${queryParams.toString()}`
          );
          if (response?.data) {
            set({ droppieRevenueTrendline: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching droppie revenue", { error });
          set({ isDroppieRevenueError: true });
        } finally {
          set({ isLoadingDroppieRevenue: false });
        }
      },
      getWithdrawsPerLocationTrendingLine: async (
        period: "month" | "week" | "quarter" | "year",
        fromToParams: {
          fromYear: number;
          fromMonth?: number;
          fromWeek?: number;
          fromQuarter?: number;
          toYear: number;
          toMonth?: number;
          toWeek?: number;
          toQuarter?: number;
        },
        type?: "location" | "city",
        selectedPlaces?: string[]
      ) => {
        set({ isLoadingWithdrawsPerLocation: true, withdrawsPerLocationTrendline: null, isWithdrawsPerLocationError: false });
        try {
          const params: Record<string, string> = {
            period,
            fromYear: fromToParams.fromYear.toString(),
            toYear: fromToParams.toYear.toString(),
          };
          if (period === "month" || period === "year") {
            params.fromMonth = (fromToParams.fromMonth ?? 1).toString();
            params.toMonth = (fromToParams.toMonth ?? 12).toString();
          } else if (period === "week") {
            params.fromWeek = (fromToParams.fromWeek ?? 1).toString();
            params.toWeek = (fromToParams.toWeek ?? 53).toString();
          } else {
            params.fromQuarter = (fromToParams.fromQuarter ?? 1).toString();
            params.toQuarter = (fromToParams.toQuarter ?? 4).toString();
          }
          if (type) params.type = type;
          if (selectedPlaces && selectedPlaces.length > 0) {
            params[type === "city" ? "cities" : "locations"] = selectedPlaces.join(",");
          }
          const queryParams = new URLSearchParams(params);
          const response = await axios.get(
            `/withdraws/stats/by-location?${queryParams.toString()}`
          );
          if (response?.data) {
            set({ withdrawsPerLocationTrendline: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching withdraws per location", { error });
          set({ isWithdrawsPerLocationError: true });
        } finally {
          set({
            isLoadingWithdrawsPerLocation: false,
            withdrawsPerLocationFirstLoadDone: true,
          });
        }
      },
      getDonationsPerCommunityCumulative: async (
        period: "month" | "week" | "quarter" | "year",
        fromToParams: {
          fromYear: number;
          fromMonth?: number;
          fromWeek?: number;
          fromQuarter?: number;
          toYear: number;
          toMonth?: number;
          toWeek?: number;
          toQuarter?: number;
        },
        cities?: string[]
      ) => {
        set({
          isLoadingDonationsPerCommunity: true,
          donationsPerCommunityCumulative: null,
          isDonationsPerCommunityError: false,
        });
        try {
          const params: Record<string, string> = {
            period,
            fromYear: fromToParams.fromYear.toString(),
            toYear: fromToParams.toYear.toString(),
          };
          if (period === "month" || period === "year") {
            params.fromMonth = (fromToParams.fromMonth ?? 1).toString();
            params.toMonth = (fromToParams.toMonth ?? 12).toString();
          } else if (period === "week") {
            params.fromWeek = (fromToParams.fromWeek ?? 1).toString();
            params.toWeek = (fromToParams.toWeek ?? 53).toString();
          } else {
            params.fromQuarter = (fromToParams.fromQuarter ?? 1).toString();
            params.toQuarter = (fromToParams.toQuarter ?? 4).toString();
          }
          params.type = "city";
          if (cities && cities.length > 0) {
            params.cities = cities.join(",");
          }
          const queryParams = new URLSearchParams(params);
          const response = await axios.get(
            `/withdraws/stats/donations-per-community-cumulative?${queryParams.toString()}`
          );
          if (response?.data) {
            set({ donationsPerCommunityCumulative: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching donations per community cumulative", {
            error,
          });
          set({ isDonationsPerCommunityError: true });
        } finally {
          set({ isLoadingDonationsPerCommunity: false });
        }
      },
      getWalletAndDonationsCumulative: async (
        period: "month" | "week" | "quarter" | "year",
        fromToParams: {
          fromYear: number;
          fromMonth?: number;
          fromWeek?: number;
          fromQuarter?: number;
          toYear: number;
          toMonth?: number;
          toWeek?: number;
          toQuarter?: number;
        }
      ) => {
        set({
          isLoadingWalletAndDonations: true,
          walletAndDonationsCumulative: null,
          isWalletAndDonationsError: false,
        });
        try {
          const params: Record<string, string> = {
            period,
            fromYear: fromToParams.fromYear.toString(),
            toYear: fromToParams.toYear.toString(),
          };
          if (period === "month" || period === "year") {
            params.fromMonth = (fromToParams.fromMonth ?? 1).toString();
            params.toMonth = (fromToParams.toMonth ?? 12).toString();
          } else if (period === "week") {
            params.fromWeek = (fromToParams.fromWeek ?? 1).toString();
            params.toWeek = (fromToParams.toWeek ?? 53).toString();
          } else {
            params.fromQuarter = (fromToParams.fromQuarter ?? 1).toString();
            params.toQuarter = (fromToParams.toQuarter ?? 4).toString();
          }
          const queryParams = new URLSearchParams(params);
          const response = await axios.get(
            `/withdraws/stats/wallet-and-donations-cumulative?${queryParams.toString()}`
          );
          if (response?.data) {
            set({ walletAndDonationsCumulative: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching wallet and donations cumulative", {
            error,
          });
          set({ isWalletAndDonationsError: true });
        } finally {
          set({ isLoadingWalletAndDonations: false });
        }
      },
      getUsersWithMoneyInWalletPerLocation: async (
        period: "month" | "week" | "quarter" | "year",
        fromToParams: {
          fromYear: number;
          fromMonth?: number;
          fromWeek?: number;
          fromQuarter?: number;
          toYear: number;
          toMonth?: number;
          toWeek?: number;
          toQuarter?: number;
        },
        type?: "location" | "city",
        selectedPlaces?: string[]
      ) => {
        set({
          isLoadingUsersWithMoneyPerLocation: true,
          usersWithMoneyPerLocation: null,
          isUsersWithMoneyPerLocationError: false,
        });
        try {
          const params: Record<string, string> = {
            period,
            fromYear: fromToParams.fromYear.toString(),
            toYear: fromToParams.toYear.toString(),
          };
          if (period === "month" || period === "year") {
            params.fromMonth = (fromToParams.fromMonth ?? 1).toString();
            params.toMonth = (fromToParams.toMonth ?? 12).toString();
          } else if (period === "week") {
            params.fromWeek = (fromToParams.fromWeek ?? 1).toString();
            params.toWeek = (fromToParams.toWeek ?? 53).toString();
          } else {
            params.fromQuarter = (fromToParams.fromQuarter ?? 1).toString();
            params.toQuarter = (fromToParams.toQuarter ?? 4).toString();
          }
          if (type) params.type = type;
          if (selectedPlaces && selectedPlaces.length > 0) {
            params[type === "city" ? "cities" : "locations"] =
              selectedPlaces.join(",");
          }
          const queryParams = new URLSearchParams(params);
          const response = await axios.get(
            `/withdraws/stats/users-with-money-per-location?${queryParams.toString()}`
          );
          if (response?.data) {
            set({ usersWithMoneyPerLocation: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching users with money per location", {
            error,
          });
          set({ isUsersWithMoneyPerLocationError: true });
        } finally {
          set({ isLoadingUsersWithMoneyPerLocation: false });
        }
      },
      getDroppieRevenueTrendingDrops: async (
        requestedDay = new Date(),
        period = "lastTwelweMonths"
      ) => {
        useAppLoading.getState().setIsLoading(true);
        const currentLocation = useCurrentLocation.getState().currentLocation;
        try {
          const response = await axios.get(
            `/drops/market/stats/droppie-revenue-trending?requestedDay=${requestedDay}&period=${period}&location=${currentLocation}`
          );
          if (response?.data) {
            set({ droppieRevenueTrendingDrops: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching drops trending dashboard stats", {
            error,
          });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      getTotalDroppieRevenueTrendingDrops: async (
        requestedDay = new Date(),
        period = "yearly"
      ) => {
        useAppLoading.getState().setIsLoading(true);
        const currentLocation = useCurrentLocation.getState().currentLocation;
        try {
          const response = await axios.get(
            `/drops/market/stats/total-droppie-revenue-trending?requestedDay=${requestedDay}&period=${period}&location=${currentLocation}`
          );
          if (response?.data) {
            set({ totalDroppieRevenueTrendingDrops: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching total drops trending dashboard stats", {
            error,
          });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      getDropsStats: async (requestedYear, requestedDay = new Date()) => {
        useAppLoading.getState().setIsLoading(true);
        const currentLocation = useCurrentLocation.getState().currentLocation;
        try {
          const response = await axios.get(
            `/dashboard-stats/drops/volumes?requestedYear=${requestedYear}&requestedDay=${requestedDay}&location=${currentLocation}`
          );
          if (response?.data) {
            set({ dropsStats: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching drops dashboard stats", { error });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      getDropsStatsByLocation: async (
        period = "monthly",
        date = new Date()
      ) => {
        useAppLoading.getState().setIsLoading(true);
        try {
          const response = await axios.get(
            `/dashboard-stats/drops/location-volumes?period=${period}&date=${date}`
          );
          if (response?.data) {
            set({ dropsLocationStats: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching drops dashboard stats", { error });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      getStreamCollectedStats: async (requestedDay) => {
        useAppLoading.getState().setIsLoading(true);
        const currentLocation = useCurrentLocation.getState().currentLocation;
        try {
          const response = await axios.get(
            `/dashboard-stats/stream-collected?requestedDay=${requestedDay}&location=${currentLocation}`
          );
          if (response?.data) {
            set({ streamCollectedStats: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching stream collected stats", { error });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      getStaticStats: async (period, startDate, endDate, streamFilters) => {
        useAppLoading.getState().setIsLoading(true);
        const currentLocation = useCurrentLocation.getState().currentLocation;
        try {
          const response = await axios.get(
            `/dashboard-stats/static?location=${currentLocation}&period=${period}&startDate=${startDate}&endDate=${endDate}&streamFilters=${streamFilters}`
          );
          if (response?.data) {
            set({ dashboardStats: response.data });
          }
        } catch (error) {
          LOG.error("Error on fetching statis dashboard stats", { error });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      getRevenuePerCustomerStats: async (
        period = "yearly",
        year,
        quarter,
        month,
        week,
        requestedDay = new Date(),
        selectedStores
      ) => {
        useAppLoading.getState().setIsLoading(true);
        try {
          let url = `/locations/stats/revenue-per-customer?period=${period}&requestedDay=${requestedDay}`;

          if (year) url += `&year=${year}`;
          if (quarter) url += `&quarter=${quarter}`;
          if (month) url += `&month=${month}`;
          if (week) url += `&week=${week}`;
          if (selectedStores && selectedStores.length > 0) {
            url += `&selectedStores=${selectedStores.join(",")}`;
          }

          const response = await axios.get(url);
          if (response?.data) {
            set({ revenuePerCustomerStats: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching revenue per customer stats", { error });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      getActiveUsers: async (requestedDay = new Date(), dropsCount = 1) => {
        useAppLoading.getState().setIsLoading(true);
        const currentLocation = useCurrentLocation.getState().currentLocation;
        try {
          const response = await axios.get(
            `/drops/user-stats/active-users?requestedDay=${requestedDay}&location=${currentLocation}&dropsCount=${dropsCount}`
          );
          if (response?.data) {
            set({ activeUsers: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching active users", { error });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
    }),
    {
      name: "dashboard-stats",
      storage: createJSONStorage(() => localStorage),
        partialize: (state) =>
          Object.fromEntries(
            Object.entries(state).filter(
              ([key]) =>
                key !== "isLoadingPayoutToConsumer" &&
                key !== "withdrawsPerLocationTrendline" &&
                key !== "isLoadingWithdrawsPerLocation" &&
                key !== "withdrawsPerLocationFirstLoadDone" &&
                key !== "usersWithMoneyPerLocation" &&
                key !== "isLoadingUsersWithMoneyPerLocation" &&
                key !== "donationsPerCommunityCumulative" &&
                key !== "isLoadingDonationsPerCommunity" &&
                key !== "walletAndDonationsCumulative" &&
                key !== "isLoadingWalletAndDonations" &&
                key !== "isWalletAndDonationsError"
            )
          ),
    }
  )
);
